import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { Mail, Phone, MessageSquare, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const INTENT_PREFILL = {
  space: "Hi — I'd like to discuss building a custom website or app with Geminii Space. My business is …",
  "early-access": "Hi — I'd like to be added to the early-access list for a Geminii Space app. The vertical I'm interested in is …",
  trial: "Hi — I'd like to start a free trial of Rogue. My business is …",
  nexus: "Hi — I'd like a quote for Geminii Nexus. The plant / site / process is …\nApprox. number of nodes: …\nApprox. number of sites: …",
  "nexus-call": "Hi — I'd like to schedule a technical call with the Nexus engineering team. The deployment context is …",
};

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const intent = searchParams.get("intent");
  const [form, setForm] = useState({
    name: "",
    email: "",
    message: intent && INTENT_PREFILL[intent] ? INTENT_PREFILL[intent] : "",
    phone: "",
  });
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (intent && INTENT_PREFILL[intent] && !form.message) {
      setForm((f) => ({ ...f, message: INTENT_PREFILL[intent] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await axios.post(`${API}/api/leads/contact`, form);
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.response?.data?.detail || "Something went wrong.");
    }
  };

  return (
    <div className="min-h-screen" data-testid="contact-page">
      <section className="pt-28 md:pt-36 pb-12 md:pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left: Info */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <h1 className="font-display font-bold text-4xl sm:text-5xl text-white leading-tight mb-6">
                Get in touch
              </h1>
              <p className="text-lg text-geminii-muted mb-12 max-w-md">
                Whether you're ready to start or just have questions — we'd like to hear from you.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <Mail size={20} className="text-rogue-cyan mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-white">Email</p>
                    <a href="mailto:hello@geminii-ai.com" className="text-sm text-geminii-muted hover:text-rogue-cyan transition-colors">
                      hello@geminii-ai.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <MessageSquare size={20} className="text-rogue-cyan mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-white">WhatsApp</p>
                    <a href="https://wa.me/27000000000" target="_blank" rel="noopener noreferrer" className="text-sm text-geminii-muted hover:text-rogue-cyan transition-colors">
                      Chat with us on WhatsApp
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right: Form */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
              {status === "success" ? (
                <div className="bg-geminii-card border border-white/[0.06] rounded-xl p-10 text-center" data-testid="contact-success">
                  <CheckCircle className="mx-auto mb-4 text-rogue-cyan" size={48} />
                  <h3 className="font-display font-bold text-xl text-white mb-2">Message sent.</h3>
                  <p className="text-geminii-muted text-sm">We'll get back to you shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-geminii-card border border-white/[0.06] rounded-xl p-8 space-y-5" data-testid="contact-form">
                  <div>
                    <label className="text-xs font-medium text-geminii-muted mb-1.5 block">Your name</label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="contact-input-name"
                      className="w-full px-4 py-3 rounded-lg bg-geminii-deep border border-white/[0.08] text-white text-sm placeholder:text-geminii-dim focus:outline-none focus:border-rogue-cyan focus:ring-1 focus:ring-rogue-cyan transition-all"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-geminii-muted mb-1.5 block">Email</label>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="contact-input-email"
                      className="w-full px-4 py-3 rounded-lg bg-geminii-deep border border-white/[0.08] text-white text-sm placeholder:text-geminii-dim focus:outline-none focus:border-rogue-cyan focus:ring-1 focus:ring-rogue-cyan transition-all"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-geminii-muted mb-1.5 block">Phone (optional)</label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      data-testid="contact-input-phone"
                      className="w-full px-4 py-3 rounded-lg bg-geminii-deep border border-white/[0.08] text-white text-sm placeholder:text-geminii-dim focus:outline-none focus:border-rogue-cyan focus:ring-1 focus:ring-rogue-cyan transition-all"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-geminii-muted mb-1.5 block">Message</label>
                    <textarea
                      required
                      rows={4}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      data-testid="contact-input-message"
                      className="w-full px-4 py-3 rounded-lg bg-geminii-deep border border-white/[0.08] text-white text-sm placeholder:text-geminii-dim focus:outline-none focus:border-rogue-cyan focus:ring-1 focus:ring-rogue-cyan transition-all resize-none"
                    />
                  </div>

                  {status === "error" && (
                    <div className="flex items-center gap-2 text-rogue-rose text-xs bg-rogue-rose/10 rounded-lg px-3 py-2">
                      <AlertCircle size={14} />
                      {errorMsg}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === "loading"}
                    data-testid="contact-submit-btn"
                    className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-rogue-cyan text-geminii-deep text-sm font-semibold hover:shadow-[0_0_20px_rgba(31,182,193,0.4)] transition-all duration-250 disabled:opacity-50"
                  >
                    {status === "loading" ? <Loader2 size={16} className="animate-spin" /> : "Send message"}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
